import { CommonModule, formatDate } from '@angular/common';
import { Component, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressBarModule } from 'primeng/progressbar';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { DialogService } from 'primeng/dynamicdialog';
import { DialogModule } from 'primeng/dialog';
import { finalize } from 'rxjs';

import { ShipmentFileUploadComponent } from '../../../helpers/shipment-file-upload/shipment-file-upload.component';
import { AttachmentPreviewComponent } from '../../../shared/attachment-preview/attachment-preview.component';
import { PayloadItemView } from '../../../models/meta-params/payload-item-view';
import { PermissionService } from '../../../security/permission.service';
import { app_permissions } from '../../../security/app-permissions';
import { SessionStorageService } from '../../../services/session-storage.service';
import { ShipmentFileDownloadUrlCallback } from '../../../services/google/google-cloud-storage.service';
import { PageDataPanelComponent } from '../../../shared/page-layout/page-data-panel.component';
import { PageDetailHeroComponent } from '../../../shared/page-layout/page-detail-hero.component';
import { PageMetricStripComponent } from '../../../shared/page-layout/page-metric-strip.component';
import { PageMetric } from '../../../shared/page-layout/page-layout.models';
import { PageShellComponent } from '../../../shared/page-layout/page-shell.component';
import { ShipmentDelivery } from '../../models/delivery/shipment-delivery.model';
import { ShipmentDeliveryActionData } from '../../models/shipment-action-data.model';
import { ShipmentDeliveryAttachment } from '../../models/shipment-delivery.attachment';
import { Shipment } from '../../models/shipment';
import { ShipmentDeliveryStop, ShipmentDeliveryStopLine } from '../../models/workflow/shipment-delivery-stop.model';
import { ShipmentDeliveryOperationStatus, ShipmentEvidence, ShipmentOperationTimelineEvent } from '../../models/workflow/shipment-workflow.model';
import { ShipmentService } from '../../shipment.service';

interface PayloadBalanceRow {
    shipmentLineId: number;
    label: string;
    plannedPickup: number;
    plannedDropoff: number;
    actualPickup: number;
    actualDropoff: number;
    plannedBalance: number;
    actualBalance: number;
}

interface SiteOperationSelectOption {
    label: string;
    value: number;
}

@Component({
    selector: 'app-delivery-site-operation',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        RouterModule,
        ToastModule,
        BreadcrumbModule,
        TagModule,
        ProgressBarModule,
        ButtonModule,
        TableModule,
        IftaLabelModule,
        InputGroupModule,
        InputGroupAddonModule,
        InputNumberModule,
        InputTextModule,
        SelectModule,
        TextareaModule,
        DialogModule,
        PageShellComponent,
        PageDetailHeroComponent,
        PageMetricStripComponent,
        PageDataPanelComponent,
        AttachmentPreviewComponent
    ],
    providers: [MessageService, DialogService, ShipmentService],
    templateUrl: './delivery-site-operation.component.html',
    styleUrls: ['./delivery-site-operation.component.css']
})
export class DeliverySiteOperationComponent implements OnInit {
    protected readonly app_permissions = app_permissions;
    deliveryId: number;
    stopId: number;
    private pageLoadRequestToken = 0;
    shipment: Shipment;
    shipmentDelivery: ShipmentDelivery;
    stops: ShipmentDeliveryStop[] = [];
    selectedStop: ShipmentDeliveryStop;
    operationStatus: ShipmentDeliveryOperationStatus | null = null;
    workflowEvents: ShipmentOperationTimelineEvent[] = [];
    stopEvidenceAttachments: ShipmentDeliveryAttachment[] = [];
    deliveryEvidence: ShipmentEvidence[] = [];
    actionData = signal<ShipmentDeliveryActionData | null>(null);

    isLoading = true;
    isSavingLines = false;
    isLoadingStopEvidence = false;
    isLoadingPayloadEvidence = false;
    transitioningStopLineIds = new Set<number>();
    rejectionDialogVisible = false;
    rejectionLine: ShipmentDeliveryStopLine | null = null;
    rejectionSaving = false;
    rejectionAcceptedQuantity = 0;
    rejectionRejectedQuantity = 0;
    rejectionReasonCode = '';
    rejectionRemarks = '';
    rejectionReasons: Array<{ label: string; value: string }> = [];
    reasonsLoading = false;
    reasonsError = '';
    decisionError = '';
    decisionAction: 'REJECT' | 'RESCHEDULE' | 'SKIP' = 'REJECT';
    decisionTarget: 'LINE' | 'SITE' = 'LINE';
    decisionScope: 'FULL' | 'PARTIAL' = 'FULL';
    selectedAfterStopId?: number;
    decisionRequestId = '';
    pickupAllocations: Array<{ stopLineId: number; label: string; quantity: number; available: number }> = [];
    sitePickupAllocations: Array<{ sourceLineId: number; sourceLabel: string; stopLineId: number;
        label: string; quantity: number; available: number; required: number }> = [];
    reasonManagerVisible = false;
    reasonManagerSaving = false;
    reasonManagerLoading = false;
    reasonManagerError = '';
    overrideDialogVisible = false;
    overrideMessage = '';
    overrideReason = '';
    private overrideRetry?: (reason: string) => void;
    managedReasons: Array<{ reasonId: number; reasonCode: string; label: string; active: boolean; sortOrder: number }> = [];
    editingReasonId: number | null = null;
    reasonForm = { reasonCode: '', label: '', active: true, sortOrder: 0 };
    breadcrumbItems: MenuItem[] = [];
    homeBreadcrumb: MenuItem = { icon: 'pi pi-home', routerLink: '/dashboard' };

    readonly pickupPayloadStateOptions = ['PICKED', 'LOADED'];
    readonly dropoffPayloadStateOptions = ['DELIVERED'];

    newLineShipmentLineId: number | undefined;
    newLineCounterpartStopId: number | undefined;
    newLineAction = 'PICKUP';
    newLinePlannedQuantity = 1;

    constructor(
        private router: Router,
        private route: ActivatedRoute,
        private shipmentService: ShipmentService,
        private messageService: MessageService,
        private sessionStorageService: SessionStorageService,
        private dialogService: DialogService,
        public permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        this.route.params.subscribe(params => {
            this.deliveryId = Number(params['deliveryId']);
            this.stopId = Number(params['stopId']);
            this.loadPage();
        });
    }

    loadPage(): void {
        const requestedDeliveryId = this.deliveryId;
        const requestedStopId = this.stopId;
        const requestToken = ++this.pageLoadRequestToken;
        this.isLoading = true;
        this.shipmentService.getShipmentAndDeliveryByDeliveryId(requestedDeliveryId)
            .pipe(finalize(() => {
                if (requestToken === this.pageLoadRequestToken) {
                    this.isLoading = false;
                }
            }))
            .subscribe({
                next: shipment => {
                    if (requestToken !== this.pageLoadRequestToken
                        || requestedDeliveryId !== this.deliveryId
                        || requestedStopId !== this.stopId) {
                        return;
                    }
                    const delivery = shipment?.deliveries?.find(item => item.deliveryId === requestedDeliveryId);
                    if (!shipment || !delivery) {
                        this.handleLoadFailure();
                        return;
                    }
                    this.shipment = shipment;
                    this.shipmentDelivery = delivery;
                    this.updateBreadcrumbItems();
                    this.loadStops();
                    this.loadOperationStatus();
                    this.loadActionData();
                    this.loadDeliveryEvidence();
                },
                error: () => {
                    if (requestToken === this.pageLoadRequestToken
                        && requestedDeliveryId === this.deliveryId
                        && requestedStopId === this.stopId) {
                        this.handleLoadFailure();
                    }
                }
            });
    }

    handleLoadFailure(): void {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to load site operation details.' });
        this.router.navigate(['/shipment/delivery/view', this.deliveryId]);
    }

    loadStops(): void {
        if (!this.shipment?.shipmentId || !this.deliveryId) {
            return;
        }
        this.shipmentService.getDeliveryStops(this.shipment.shipmentId, this.deliveryId).subscribe({
            next: stops => {
                this.stops = stops ?? [];
                this.selectedStop = this.stops.find(stop => stop.stopId === this.stopId)!;
                if (!this.selectedStop) {
                    this.handleLoadFailure();
                    return;
                }
                this.selectedStop.lineItems = this.selectedStop.lineItems ?? [];
                this.updateBreadcrumbItems();
                this.newLineAction = this.defaultActionForStop();
                this.autoSelectCounterpartStop();
                this.loadStopEvidenceAttachments();
            },
            error: () => this.handleLoadFailure()
        });
    }

    loadStopEvidenceAttachments(): void {
        this.stopEvidenceAttachments = this.evidenceAttachmentsFromDelivery();
        const shipmentId = this.shipment?.shipmentId;
        const stopId = this.selectedStop?.stopId;
        if (!shipmentId || !this.deliveryId || !stopId) {
            return;
        }
        this.isLoadingStopEvidence = true;
        this.shipmentService.getDeliveryStopAttachments(shipmentId, this.deliveryId, stopId)
            .pipe(finalize(() => this.isLoadingStopEvidence = false))
            .subscribe({
                next: files => this.stopEvidenceAttachments = this.filterEvidenceAttachments([
                    ...(files ?? []),
                    ...this.evidenceAttachmentsFromDelivery()
                ]),
                error: error => {
                    this.messageService.add({
                        severity: 'warn',
                        summary: 'Evidence unavailable',
                        detail: this.errorMessage(error, 'Uploaded evidence could not be loaded for this stop.')
                    });
                }
            });
    }

    loadOperationStatus(): void {
        if (!this.shipment?.shipmentId || !this.deliveryId) {
            return;
        }
        this.shipmentService.getDeliveryOperationStatus(this.shipment.shipmentId, this.deliveryId).subscribe({
            next: status => {
                this.operationStatus = status;
                this.workflowEvents = status?.timelineEvents ?? [];
                if (status?.deliveryStatus) {
                    this.shipmentDelivery.workflowState = status.deliveryStatus;
                }
            }
        });
    }

    loadActionData(): void {
        if (!this.shipment?.shipmentId || !this.deliveryId) {
            return;
        }
        this.shipmentService.getDeliveryActionData(this.shipment.shipmentId, this.deliveryId).subscribe({
            next: data => this.actionData.set(data)
        });
    }

    loadDeliveryEvidence(): void {
        if (!this.shipment?.shipmentId || !this.deliveryId) {
            return;
        }
        this.isLoadingPayloadEvidence = true;
        this.shipmentService.getDeliveryEvidence(this.shipment.shipmentId, this.deliveryId)
            .pipe(finalize(() => this.isLoadingPayloadEvidence = false))
            .subscribe({
                next: evidence => this.deliveryEvidence = evidence ?? [],
                error: () => this.deliveryEvidence = []
            });
    }

    siteMetrics(): PageMetric[] {
        return [
            { label: 'Shipment Status', value: this.operationStatus?.shipmentStatus || this.shipment?.workflowState || this.shipment?.shipmentStatus || 'DRAFT', hint: 'Shipment level', tone: 'info' },
            { label: 'Delivery Status', value: this.operationStatus?.deliveryStatus || this.shipmentDelivery?.workflowState || 'PLANNED', hint: 'Route level', tone: 'info' },
            { label: 'Site Status', value: this.selectedStop?.workflowState || 'PENDING', hint: this.selectedStop?.evidenceStatus || 'Evidence pending', tone: 'info' },
            { label: 'Order Release', value: this.orderReleaseStatusLabel(), hint: 'Planning release', tone: 'default' }
        ];
    }

    siteHeroEyebrow(): string {
        if (this.selectedStop?.stopPurpose === 'REJECTION_RETURN') return `Return handoff #${this.selectedStop.stopNumber ?? '-'}`;
        return `${this.displayState(this.selectedStop?.stopType || 'STOP')} Stop #${this.selectedStop?.stopNumber ?? '-'}`;
    }

    hasRejectionReturn(): boolean {
        return this.stops.some(stop => stop.stopPurpose === 'REJECTION_RETURN');
    }

    siteHeroTitle(): string {
        return this.selectedStop?.locationName || 'Delivery site';
    }

    siteHeroSubtitle(): string {
        return `Delivery # ${this.shipmentDelivery?.deliveryNumber || this.deliveryId}`;
    }

    goBackToDelivery(): void {
        this.router.navigate(['/shipment/delivery/view', this.deliveryId]);
    }

    payloadSelectOptions(): SiteOperationSelectOption[] {
        const options: SiteOperationSelectOption[] = [];
        this.payloadItems().forEach(payload => {
            const value = this.payloadLineId(payload);
            if (value !== undefined) {
                options.push({ value, label: this.payloadLabelByLineId(value) });
            }
        });
        return options;
    }

    counterpartStopOptions(): SiteOperationSelectOption[] {
        const options: SiteOperationSelectOption[] = [];
        this.counterpartStops().forEach(stop => {
            if (stop.stopId !== undefined) {
                options.push({
                    value: stop.stopId,
                    label: `#${stop.stopNumber} ${stop.stopType} - ${stop.locationName || 'Location'}`
                });
            }
        });
        return options;
    }

    private updateBreadcrumbItems(): void {
        const shipmentLabel = this.shipment?.shipmentNumber || 'Shipment';
        const deliveryLabel = this.shipmentDelivery?.deliveryNumber || `Delivery ${this.deliveryId}`;
        this.breadcrumbItems = [
            { label: 'Shipments', routerLink: '/shipment/company' },
            { label: shipmentLabel, routerLink: this.shipment?.shipmentId ? ['/shipment/view', this.shipment.shipmentId] : undefined },
            { label: deliveryLabel, routerLink: ['/shipment/delivery/view', this.deliveryId] },
            { label: this.stopBreadcrumbLabel() }
        ];
    }

    private stopBreadcrumbLabel(): string {
        const locationName = this.selectedStop?.locationName?.trim();
        if (locationName) {
            return locationName;
        }
        if (this.selectedStop?.stopNumber) {
            return `${this.displayState(this.selectedStop.stopType || 'STOP')} #${this.selectedStop.stopNumber}`;
        }
        return 'Site operation';
    }

    orderReleaseStatusLabel(): string {
        const releases = this.operationStatus?.orderReleases ?? [];
        if (!releases.length) return 'Not linked';
        return Array.from(new Set(releases.map(release => release.releaseStatus || 'PENDING'))).join(', ');
    }

    payloadItems(): PayloadItemView[] {
        return this.shipment?.payloadItemList ?? [];
    }

    payloadAssignmentWarning(): string | null {
        if (this.hasAnyStopLine() || (this.shipmentDelivery?.payloadItemList ?? []).length > 0) {
            return null;
        }
        if (!this.payloadItems().length || this.activeDeliveryCount() <= 1) {
            return null;
        }
        return 'Payload lines were not auto-created because this shipment has multiple active deliveries and this delivery has no payload assignment. Assign payload to this delivery or add site lines manually.';
    }

    payloadLineId(item: PayloadItemView): number | undefined {
        return item.shipmentLineId ?? item.packingListIndex;
    }

    payloadLabelByLineId(shipmentLineId?: number): string {
        const item = this.payloadItems().find(payload => this.payloadLineId(payload) === shipmentLineId);
        if (!item) {
            return shipmentLineId ? `Payload line ${shipmentLineId}` : 'Payload line';
        }
        const type = item.itemType?.name || 'Payload';
        const quantity = item.itemQuantity?.quantity;
        const unit = item.itemQuantity?.uom?.name || '';
        const itemNumber = item.warehouseItemNumber ? `Item ${item.warehouseItemNumber}` : undefined;
        const sku = item.skuNumber ? `SKU ${item.skuNumber}` : undefined;
        const customerItem = item.customerItemNumber ? `Customer ${item.customerItemNumber}` : undefined;
        const size = item.itemSize ? `Size ${item.itemSize}` : undefined;
        return [type, item.warehouseItemName || item.itemDescription || item.itemDesciption, itemNumber, sku, customerItem, size, quantity ? `${quantity} ${unit}`.trim() : undefined]
            .filter(Boolean)
            .join(' - ');
    }

    payloadUnitByLineId(shipmentLineId?: number): string {
        const item = this.payloadItems().find(payload => this.payloadLineId(payload) === shipmentLineId);
        return item?.itemQuantity?.uom?.name || '';
    }

    isCancelled(): boolean {
        const shipmentState = String(this.shipment?.workflowState || this.shipment?.shipmentStatus || '').toUpperCase();
        const deliveryState = String(this.shipmentDelivery?.workflowState || '').toUpperCase();
        const jobStatus = String(this.shipmentDelivery?.jobStatus || '').toUpperCase();
        return shipmentState === 'CANCELLED'
            || deliveryState === 'CANCELLED'
            || deliveryState === 'REJECTED'
            || jobStatus === 'JOB_DRIVER_CANCELLED'
            || jobStatus === 'JOB_DRIVER_REJECTED';
    }

    isPayloadPlanLocked(line?: ShipmentDeliveryStopLine): boolean {
        return this.isCancelled() || !['PENDING', 'ARRIVED'].includes(String(this.selectedStop?.workflowState || '').toUpperCase())
            || (line != null && (line.workflowState !== 'PLANNED' || Number(line.actualQuantity ?? 0) > 0
                || Number(line.rejectedQuantity ?? 0) > 0 || Number(line.rescheduledQuantity ?? 0) > 0));
    }

    addStopLine(): void {
        if (this.isCancelled()) {
            return;
        }
        if (!this.selectedStop || !this.newLineShipmentLineId) {
            this.messageService.add({ severity: 'warn', summary: 'Missing payload', detail: 'Select a payload item before adding a stop line.' });
            return;
        }
        const plannedQuantity = Number(this.newLinePlannedQuantity || 0);
        if (!Number.isFinite(plannedQuantity) || plannedQuantity <= 0) {
            this.messageService.add({ severity: 'warn', summary: 'Invalid quantity', detail: 'Planned Qty must be greater than zero.' });
            return;
        }
        if (this.hasStopLine(this.selectedStop, this.newLineShipmentLineId, this.newLineAction)) {
            this.messageService.add({ severity: 'warn', summary: 'Duplicate line', detail: 'This payload already has an operation line for this stop.' });
            return;
        }
        this.selectedStop.lineItems = this.selectedStop.lineItems ?? [];
        this.selectedStop.lineItems.push({
            shipmentId: this.shipment.shipmentId,
            deliveryId: this.deliveryId,
            stopId: this.stopId,
            shipmentLineId: this.newLineShipmentLineId,
            deliveryLineId: this.findDeliveryLineId(this.newLineShipmentLineId),
            action: this.newLineAction,
            plannedQuantity,
            actualQuantity: 0,
            workflowState: 'PLANNED'
        });
        this.addCounterpartLineIfSelected();
        this.newLinePlannedQuantity = 1;
    }

    saveStopLines(): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.selectedStop?.stopId) {
            return;
        }
        const invalidLine = this.allStopLines().find(line => Number(line.plannedQuantity ?? 0) <= 0 && Number(line.actualQuantity ?? 0) <= 0);
        if (invalidLine) {
            this.messageService.add({ severity: 'warn', summary: 'Invalid quantity', detail: 'Remove empty payload operation lines or enter a planned quantity greater than zero.' });
            return;
        }
        this.isSavingLines = true;
        this.shipmentService.replaceDeliveryRouteStopLines(this.shipment.shipmentId, this.deliveryId, this.validStopLinesForSave())
            .pipe(finalize(() => this.isSavingLines = false))
            .subscribe({
                next: stops => {
                    this.stops = stops ?? [];
                    this.selectedStop = this.stops.find(stop => stop.stopId === this.stopId)!;
                    this.updateBreadcrumbItems();
                    this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Payload operations updated.' });
                },
                error: error => this.showGateOrValidationError(error, 'Payload assignment was rejected.')
            });
    }

    transitionStop(targetState: string, overrideReason?: string): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.selectedStop?.stopId) {
            return;
        }
        this.shipmentService.transitionDeliveryStopWorkflow(this.shipment.shipmentId, this.deliveryId, this.selectedStop.stopId, {
            targetState,
            actionCode: `SITE_${targetState}`,
            source: 'PORTAL',
            actorCustomerId: this.sessionStorageService.getCustomer()?.id,
            overrideReason
        }).subscribe({
            next: event => {
                this.selectedStop.workflowState = event.nextState;
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Site state changed to ${event.nextState}.` });
                this.loadStops();
                this.loadOperationStatus();
            },
            error: error => this.handleTransitionError(error, reason => this.transitionStop(targetState, reason))
        });
    }

    openEvidenceUpload(): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.shipmentDelivery || !this.selectedStop?.stopId) {
            if (!this.isCancelled()) {
                this.messageService.add({ severity: 'warn', summary: 'Site unavailable', detail: 'Reload this site before uploading evidence.' });
            }
            return;
        }
        this.sessionStorageService.setShipmentUploadContext(this.shipment.shipmentId, this.shipmentDelivery.deliveryId);

        let fileUploadDialog: any;
        const fileUploadCallback: ShipmentFileDownloadUrlCallback = (file: ShipmentDeliveryAttachment, batchComplete?: boolean) => {
            return this.recordStopEvidence(file, batchComplete ? fileUploadDialog : undefined);
        };

        fileUploadDialog = this.dialogService.open(ShipmentFileUploadComponent, {
            header: `${this.evidenceActionLabel()} - Stop #${this.selectedStop.stopNumber || '-'}`,
            width: 'min(48rem, calc(100vw - 1.5rem))',
            modal: true,
            data: {
                callback: fileUploadCallback,
                defaultFileType: this.evidenceUploadFileType(),
                lockFileType: true,
                stopId: this.selectedStop.stopId
            }
        });
    }

    private recordStopEvidence(file: ShipmentDeliveryAttachment, fileUploadDialog?: { close: () => void }): Promise<boolean> {
        const shipmentId = this.shipment?.shipmentId;
        const stopId = this.selectedStop?.stopId;
        if (!shipmentId || !stopId) {
            this.messageService.add({ severity: 'warn', summary: 'Site unavailable', detail: 'Reload this site before uploading evidence.' });
            return Promise.resolve(false);
        }
        return new Promise(resolve => this.shipmentService.addDeliveryEvidence(shipmentId, this.deliveryId, {
            shipmentId,
            deliveryId: this.deliveryId,
            stopId,
            scope: 'STOP',
            scopeId: stopId,
            evidenceType: this.evidenceType(),
            evidenceUrl: file.fileUrl,
            valueText: file.fileName,
            status: 'VERIFIED',
            createdByCustomerId: this.sessionStorageService.getCustomer()?.id
        }).subscribe({
            next: () => {
                const evidenceFile = { ...file, stopId };
                this.shipmentDelivery.filesList = this.upsertDeliveryAttachment(evidenceFile);
                this.stopEvidenceAttachments = this.upsertEvidenceAttachment(evidenceFile);
                this.selectedStop.evidenceStatus = 'VERIFIED';
                this.messageService.add({ severity: 'success', summary: 'Evidence uploaded', detail: this.evidenceSuccessMessage() });
                if (fileUploadDialog) {
                    this.loadStops();
                    this.loadOperationStatus();
                    this.loadStopEvidenceAttachments();
                    fileUploadDialog.close();
                }
                resolve(true);
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Evidence failed',
                    detail: this.errorMessage(error, 'File uploaded, but evidence could not be recorded.')
                });
                resolve(false);
            }
        }));
    }

    transitionStopLine(line: ShipmentDeliveryStopLine, targetState: string, overrideReason?: string): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.selectedStop?.stopId || !line.stopLineId) {
            if (!this.isCancelled()) {
                this.messageService.add({ severity: 'warn', summary: 'Save required', detail: 'Save the payload line before changing its state.' });
            }
            return;
        }
        if (this.transitioningStopLineIds.has(line.stopLineId)) {
            return;
        }
        if (!this.availablePayloadStateOptions(line).includes(targetState)) {
            this.messageService.add({
                severity: 'info',
                summary: 'Already recorded',
                detail: `${this.payloadStateLabel(this.currentPayloadState(line))} is the current payload status.`
            });
            return;
        }
        const actualQuantity = this.actualQuantityForPayloadTransition(line, targetState);
        if (actualQuantity === null) {
            return;
        }
        this.transitioningStopLineIds.add(line.stopLineId);
        this.shipmentService.transitionDeliveryStopLineWorkflow(this.shipment.shipmentId, this.deliveryId, this.selectedStop.stopId, line.stopLineId, {
            targetState,
            actionCode: `SITE_PAYLOAD_${targetState}`,
            source: 'PORTAL',
            actorCustomerId: this.sessionStorageService.getCustomer()?.id,
            actualQuantity,
            exceptionReason: line.exceptionReason,
            overrideReason
        }).pipe(finalize(() => this.transitioningStopLineIds.delete(line.stopLineId!))).subscribe({
            next: event => {
                line.workflowState = event.nextState;
                this.messageService.add({ severity: 'success', summary: 'Updated', detail: `Payload state changed to ${event.nextState}.` });
                this.loadStops();
                this.loadOperationStatus();
            },
            error: error => this.handleTransitionError(error, reason => this.transitionStopLine(line, targetState, reason))
        });
    }

    canRejectLine(line: ShipmentDeliveryStopLine): boolean {
        return !this.isCancelled() && !!line.stopLineId && !line.reverseShipmentId
            && this.selectedStop?.stopPurpose !== 'REJECTION_RETURN'
            && this.remainingDecisionQuantity(line) > 0
            && ['WORKING', 'EVIDENCE_PENDING'].includes(String(this.selectedStop?.workflowState || '').toUpperCase())
            && (this.permissionService.hasPermission(app_permissions.shipments.deliveries.manage_stops)
                || this.permissionService.hasPermission(app_permissions.shipments.deliveries.edit));
    }

    remainingDecisionQuantity(line: ShipmentDeliveryStopLine): number {
        return Math.max(0, Number(line.plannedQuantity ?? 0) - Number(line.actualQuantity ?? 0)
            - Number(line.rejectedQuantity ?? 0) - Number(line.rescheduledQuantity ?? 0)
            - (this.isDropoffLine(line) ? Number(line.notCollectedQuantity ?? 0) : 0));
    }

    openRejectionDialog(line: ShipmentDeliveryStopLine, action: 'REJECT' | 'RESCHEDULE' = 'REJECT'): void {
        if (!this.canRejectLine(line)) return;
        this.openDecisionDialog('LINE', action, line);
    }

    openSiteDecision(action: 'REJECT' | 'RESCHEDULE' | 'SKIP'): void {
        if (!(this.permissionService.hasPermission(app_permissions.shipments.deliveries.manage_stops)
            || this.permissionService.hasPermission(app_permissions.shipments.deliveries.edit))) return;
        if (this.isCancelled() || !this.selectedStop?.stopId || this.selectedStop.stopPurpose === 'REJECTION_RETURN') return;
        if (action === 'SKIP' && (this.selectedStop.stopPurpose === 'REVISIT'
            || !['PENDING', 'ARRIVED'].includes(String(this.selectedStop.workflowState).toUpperCase()))) return;
        if (action === 'REJECT' && !['WORKING', 'EVIDENCE_PENDING'].includes(String(this.selectedStop.workflowState).toUpperCase())) return;
        if (action === 'RESCHEDULE' && !['PENDING', 'ARRIVED', 'WORKING', 'EVIDENCE_PENDING']
            .includes(String(this.selectedStop.workflowState).toUpperCase())) return;
        this.openDecisionDialog('SITE', action, null);
    }

    private openDecisionDialog(target: 'LINE' | 'SITE', action: 'REJECT' | 'RESCHEDULE' | 'SKIP', line: ShipmentDeliveryStopLine | null): void {
        this.decisionTarget = target;
        this.decisionAction = action;
        this.rejectionLine = line;
        this.decisionScope = 'FULL';
        this.rejectionAcceptedQuantity = Number(line?.actualQuantity ?? 0);
        this.rejectionRejectedQuantity = line ? this.remainingDecisionQuantity(line) : 0;
        this.rejectionReasonCode = '';
        this.rejectionRemarks = '';
        this.selectedAfterStopId = undefined;
        this.decisionRequestId = crypto.randomUUID();
        this.pickupAllocations = line && !this.isDropoffLine(line) ? this.stops
            .filter(stop => Number(stop.stopNumber) > Number(this.selectedStop?.stopNumber)
                && stop.stopPurpose !== 'REJECTION_RETURN' && stop.workflowState !== 'SKIPPED')
            .flatMap(stop => (stop.lineItems ?? []).filter(item => item.stopLineId && this.isDropoffLine(item)
                && (line.deliveryLineId ? item.deliveryLineId === line.deliveryLineId
                    : item.shipmentLineId === line.shipmentLineId))
                .map(item => ({ stopLineId: item.stopLineId!, label: `#${stop.stopNumber} ${stop.locationName || 'Drop-off'}`,
                    quantity: 0, available: Math.max(0, Number(item.plannedQuantity ?? 0) - Number(item.notCollectedQuantity ?? 0)) })))
            : [];
        this.sitePickupAllocations = target === 'SITE' && action !== 'SKIP'
            ? this.siteOpenLines().filter(item => item.stopLineId && !this.isDropoffLine(item))
                .flatMap(item => this.stops
                    .filter(stop => Number(stop.stopNumber) > Number(this.selectedStop?.stopNumber)
                        && stop.stopPurpose !== 'REJECTION_RETURN' && stop.workflowState !== 'SKIPPED')
                    .flatMap(stop => (stop.lineItems ?? []).filter(other => other.stopLineId && this.isDropoffLine(other)
                        && (item.deliveryLineId ? other.deliveryLineId === item.deliveryLineId
                            : other.shipmentLineId === item.shipmentLineId))
                        .map(other => ({ sourceLineId: item.stopLineId!, sourceLabel: this.payloadLabelByLineId(item.shipmentLineId),
                            stopLineId: other.stopLineId!, label: `#${stop.stopNumber} ${stop.locationName || 'Drop-off'}`,
                            quantity: 0, available: Math.max(0, Number(other.plannedQuantity ?? 0) - Number(other.notCollectedQuantity ?? 0)),
                            required: this.remainingDecisionQuantity(item) })))
                .filter((row, _, all) => all.filter(other => other.sourceLineId === row.sourceLineId).length > 1
                    && row.required < all.filter(other => other.sourceLineId === row.sourceLineId)
                        .reduce((sum, other) => sum + other.available, 0)))
            : [];
        this.rejectionReasons = [];
        this.reasonsError = '';
        this.decisionError = '';
        this.rejectionDialogVisible = true;
        this.loadDecisionReasons();
    }

    loadDecisionReasons(): void {
        const companyId = this.shipment?.owningCompany?.id;
        if (!companyId) {
            this.reasonsError = 'The shipment company could not be identified. Reload the site.';
            return;
        }
        const action = this.decisionAction;
        this.reasonsLoading = true;
        this.reasonsError = '';
        this.shipmentService.getDeliverySiteReasons(companyId, action).pipe(finalize(() => this.reasonsLoading = false)).subscribe({
            next: rows => {
                if (action !== this.decisionAction) return;
                this.rejectionReasons = rows.map(row => ({ label: row.label, value: row.reasonCode }));
                if (!rows.length) this.reasonsError = 'No active reasons are available for this action.';
            },
            error: error => {
                if (action !== this.decisionAction) return;
                this.reasonsError = error?.status === 404
                    ? 'Decision reasons are not available on this server yet. Please try again after the service is updated.'
                    : this.errorMessage(error, 'Could not load the reason list.');
            }
        });
    }

    openReasonManager(): void {
        if (!this.permissionService.hasPermission(app_permissions.company.records.edit)) return;
        this.reasonManagerVisible = true;
        this.loadManagedReasons();
    }

    loadManagedReasons(): void {
        const companyId = this.shipment?.owningCompany?.id;
        if (!companyId) return;
        this.reasonManagerLoading = true;
        this.reasonManagerError = '';
        this.shipmentService.getDeliverySiteReasons(companyId, this.decisionAction, true)
            .pipe(finalize(() => this.reasonManagerLoading = false)).subscribe({
            next: rows => this.managedReasons = rows,
            error: error => this.reasonManagerError = error?.status === 404
                ? 'Company reasons are not available on this server yet. Please try again after the service is updated.'
                : this.errorMessage(error, 'Could not load company reasons.')
        });
    }

    editManagedReason(reason: typeof this.managedReasons[number]): void {
        this.editingReasonId = reason.reasonId;
        this.reasonForm = { reasonCode: reason.reasonCode, label: reason.label,
            active: reason.active, sortOrder: reason.sortOrder };
    }

    resetManagedReason(): void {
        this.editingReasonId = null;
        this.reasonForm = { reasonCode: '', label: '', active: true, sortOrder: 0 };
    }

    saveManagedReason(): void {
        const companyId = this.shipment?.owningCompany?.id;
        if (!companyId || !this.reasonForm.reasonCode.trim() || !this.reasonForm.label.trim()) return;
        this.reasonManagerSaving = true;
        this.shipmentService.saveDeliverySiteReason(companyId, this.editingReasonId,
            { actionType: this.decisionAction, reasonCode: this.reasonForm.reasonCode.trim().toUpperCase(),
                label: this.reasonForm.label.trim(), active: this.reasonForm.active, sortOrder: this.reasonForm.sortOrder })
            .pipe(finalize(() => this.reasonManagerSaving = false)).subscribe({
                next: () => {
                    this.resetManagedReason();
                    this.loadManagedReasons();
                    this.loadDecisionReasons();
                },
                error: error => this.reasonManagerError = error?.status === 404
                    ? 'Company reasons are not available on this server yet. Please try again after the service is updated.'
                    : this.errorMessage(error, 'Could not save the reason.')
            });
    }

    rejectionQuantitiesValid(): boolean {
        if (this.decisionTarget === 'SITE') return this.decisionAction === 'SKIP' || this.siteOpenLines().length > 0
            && [...new Set(this.sitePickupAllocations.map(row => row.sourceLineId))].every(id => {
                const rows = this.sitePickupAllocations.filter(row => row.sourceLineId === id);
                return Math.abs(rows.reduce((sum, row) => sum + (row.quantity || 0), 0) - rows[0].required) < 0.000001
                    && rows.every(row => row.quantity >= 0 && row.quantity <= row.available);
            });
        if (!this.rejectionLine) return false;
        const remaining = this.remainingDecisionQuantity(this.rejectionLine);
        const quantity = this.decisionScope === 'FULL' ? remaining : this.rejectionRejectedQuantity;
        if (!(remaining > 0 && (this.decisionScope === 'FULL' || (quantity > 0 && quantity < remaining)))) return false;
        return !this.needsPickupAllocation() || Math.abs(this.pickupAllocations.reduce((sum, row) => sum + (row.quantity || 0), 0) - quantity) < 0.000001
            && this.pickupAllocations.every(row => row.quantity >= 0 && row.quantity <= row.available);
    }

    needsPickupAllocation(): boolean {
        if (this.decisionTarget !== 'LINE' || !this.rejectionLine || this.isDropoffLine(this.rejectionLine)
            || this.pickupAllocations.length <= 1) return false;
        const quantity = this.decisionScope === 'FULL' ? this.remainingDecisionQuantity(this.rejectionLine) : this.rejectionRejectedQuantity;
        return quantity < this.pickupAllocations.reduce((sum, row) => sum + row.available, 0);
    }

    siteOpenLines(): ShipmentDeliveryStopLine[] {
        return (this.selectedStop?.lineItems ?? []).filter(line => this.remainingDecisionQuantity(line) > 0);
    }

    rejectedLineCount(): number {
        return (this.selectedStop?.lineItems ?? []).filter(line => Number(line.rejectedQuantity ?? 0) > 0).length;
    }

    laterStopOptions(): Array<{ label: string; value: number }> {
        const pickupLines = this.selectedStop?.stopType === 'PICKUP' ? this.selectedStop.lineItems ?? [] : [];
        const firstDependentDropoff = pickupLines.length ? Math.min(...this.stops.filter(stop =>
            Number(stop.stopNumber) > Number(this.selectedStop?.stopNumber)
            && (stop.lineItems ?? []).some(line => this.isDropoffLine(line) && pickupLines.some(source =>
                source.deliveryLineId ? source.deliveryLineId === line.deliveryLineId
                    : source.shipmentLineId === line.shipmentLineId)))
            .map(stop => Number(stop.stopNumber))) : Infinity;
        return this.stops.filter(stop => !!stop.stopId && Number(stop.stopNumber) > Number(this.selectedStop?.stopNumber)
            && Number(stop.stopNumber) < firstDependentDropoff && stop.stopPurpose !== 'REJECTION_RETURN')
            .map(stop => ({ label: `#${stop.stopNumber} ${stop.locationName || stop.stopType}`, value: stop.stopId! }));
    }

    submitRejection(): void {
        const lineId = this.rejectionLine?.stopLineId;
        const shipmentId = this.shipment?.shipmentId;
        const stopId = this.selectedStop?.stopId;
        if (!shipmentId || !stopId || !this.rejectionQuantitiesValid() || !this.rejectionReasonCode
                || this.reasonsLoading || this.reasonsError) return;
        this.rejectionSaving = true;
        this.decisionError = '';
        const request$ = this.decisionTarget === 'LINE' && lineId
            ? this.shipmentService.decideDeliverySiteLine(shipmentId, this.deliveryId, stopId, lineId, {
                action: this.decisionAction as 'REJECT' | 'RESCHEDULE', scope: this.decisionScope,
                quantity: this.decisionScope === 'PARTIAL' ? this.rejectionRejectedQuantity : undefined,
                reasonCode: this.rejectionReasonCode, remarks: this.rejectionRemarks,
                allocations: this.needsPickupAllocation() ? this.pickupAllocations
                    .filter(row => row.quantity > 0).map(row => ({ stopLineId: row.stopLineId, quantity: row.quantity })) : undefined,
                requestId: this.decisionRequestId
            })
            : this.decisionAction === 'SKIP'
                ? this.shipmentService.skipDeliverySite(shipmentId, this.deliveryId, stopId,
                    { reasonCode: this.rejectionReasonCode, afterStopId: this.selectedAfterStopId })
                : this.shipmentService.decideDeliverySite(shipmentId, this.deliveryId, stopId,
                    { action: this.decisionAction, reasonCode: this.rejectionReasonCode, remarks: this.rejectionRemarks,
                        allocations: this.sitePickupAllocations.filter(row => row.quantity > 0)
                            .map(row => ({ sourceLineId: row.sourceLineId, stopLineId: row.stopLineId, quantity: row.quantity })) });
        request$.pipe(finalize(() => this.rejectionSaving = false)).subscribe({
            next: () => {
                this.rejectionDialogVisible = false;
                this.rejectionLine = null;
                this.messageService.add({ severity: 'success', summary: 'Decision recorded',
                    detail: this.decisionAction === 'SKIP' ? 'A required revisit was added to this delivery.'
                        : 'The route and release outcome have been updated.' });
                this.loadStops();
                this.loadOperationStatus();
            },
            error: error => this.decisionError = error?.status === 404
                ? 'This action is not available on the server yet. Please try again after the service is updated.'
                : this.errorMessage(error, 'Could not record this decision.')
        });
    }

    openPayloadEvidenceUpload(line: ShipmentDeliveryStopLine): void {
        if (this.isCancelled() || !this.shipment?.shipmentId || !this.shipmentDelivery || !this.selectedStop?.stopId) {
            if (!this.isCancelled()) {
                this.messageService.add({ severity: 'warn', summary: 'Site unavailable', detail: 'Reload this site before uploading payload evidence.' });
            }
            return;
        }
        if (!line.stopLineId) {
            this.messageService.add({ severity: 'warn', summary: 'Save required', detail: 'Save the payload line before uploading evidence.' });
            return;
        }
        this.sessionStorageService.setShipmentUploadContext(this.shipment.shipmentId, this.shipmentDelivery.deliveryId);

        let fileUploadDialog: any;
        const fileUploadCallback: ShipmentFileDownloadUrlCallback = (file: ShipmentDeliveryAttachment, batchComplete?: boolean) => {
            return this.recordPayloadLineEvidence(line, file, batchComplete ? fileUploadDialog : undefined);
        };

        fileUploadDialog = this.dialogService.open(ShipmentFileUploadComponent, {
            header: `${this.payloadEvidenceActionLabel(line)} - ${this.payloadLabelByLineId(line.shipmentLineId)}`,
            width: 'min(48rem, calc(100vw - 1.5rem))',
            modal: true,
            data: {
                callback: fileUploadCallback,
                defaultFileType: this.payloadEvidenceUploadFileType(line),
                lockFileType: true,
                stopId: this.selectedStop.stopId
            }
        });
    }

    private recordPayloadLineEvidence(line: ShipmentDeliveryStopLine, file: ShipmentDeliveryAttachment, fileUploadDialog?: { close: () => void }): Promise<boolean> {
        const shipmentId = this.shipment?.shipmentId;
        const stopId = this.selectedStop?.stopId;
        const scopeId = line.deliveryLineId ?? line.stopLineId ?? line.shipmentLineId;
        if (!shipmentId || !stopId || !scopeId) {
            this.messageService.add({ severity: 'warn', summary: 'Payload unavailable', detail: 'Save this payload line before uploading evidence.' });
            return Promise.resolve(false);
        }
        const payload: ShipmentEvidence = {
            shipmentId,
            deliveryId: this.deliveryId,
            stopId,
            deliveryLineId: line.deliveryLineId,
            shipmentLineId: line.shipmentLineId,
            scope: 'PAYLOAD_LINE',
            scopeId,
            evidenceType: this.payloadEvidenceType(line),
            evidenceUrl: file.fileUrl,
            valueText: file.fileName,
            status: 'VERIFIED',
            createdByCustomerId: this.sessionStorageService.getCustomer()?.id
        };
        return new Promise(resolve => this.shipmentService.addDeliveryEvidence(shipmentId, this.deliveryId, payload).subscribe({
            next: saved => {
                const evidence = { ...payload, ...saved, createdAt: saved?.createdAt ?? new Date() };
                const evidenceFile = { ...file, stopId };
                this.deliveryEvidence = this.upsertDeliveryEvidence(evidence);
                this.shipmentDelivery.filesList = this.upsertDeliveryAttachment(evidenceFile);
                this.stopEvidenceAttachments = this.upsertEvidenceAttachment(evidenceFile);
                this.messageService.add({ severity: 'success', summary: 'Evidence uploaded', detail: this.payloadEvidenceSuccessMessage(line) });
                fileUploadDialog?.close();
                resolve(true);
            },
            error: error => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Evidence failed',
                    detail: this.errorMessage(error, 'File uploaded, but payload evidence could not be recorded.')
                });
                resolve(false);
            }
        }));
    }

    handleTransitionError(error: any, retryWithOverride: (reason: string) => void): void {
        const message = this.errorMessage(error, 'Transition blocked by workflow gates.');
        if (this.isInvalidStopTransition(message)) {
            this.messageService.add({ severity: 'warn', summary: 'Invalid action', detail: message });
            this.loadStops();
            this.loadOperationStatus();
            return;
        }
        if (this.isPayloadInputValidationError(message)) {
            this.messageService.add({ severity: 'warn', summary: 'Payload input needed', detail: message });
            return;
        }
        if (!this.permissionService.hasPermission(app_permissions.shipments.workflow_override)) {
            this.messageService.add({ severity: 'warn', summary: 'Gate failed', detail: message });
            return;
        }
        this.overrideMessage = message;
        this.overrideReason = '';
        this.overrideRetry = retryWithOverride;
        this.overrideDialogVisible = true;
    }

    confirmOverride(): void {
        const reason = this.overrideReason.trim();
        if (!reason || !this.overrideRetry) return;
        const retry = this.overrideRetry;
        this.overrideRetry = undefined;
        this.overrideDialogVisible = false;
        retry(reason);
    }

    showGateOrValidationError(error: any, fallback: string): void {
        this.messageService.add({ severity: 'warn', summary: 'Validation failed', detail: this.errorMessage(error, fallback) });
    }

    errorMessage(error: any, fallback: string): string {
        return error?.error?.message || error?.message || fallback;
    }

    canOverride(): boolean {
        return this.permissionService.hasPermission(app_permissions.shipments.workflow_override);
    }

    findDeliveryLineId(shipmentLineId?: number): number | undefined {
        for (const stop of this.stops) {
            const existing = (stop.lineItems ?? []).find(line => line.shipmentLineId === shipmentLineId && line.deliveryLineId);
            if (existing?.deliveryLineId) {
                return existing.deliveryLineId;
            }
        }
        return undefined;
    }

    actionOptionsForSelectedStop(): string[] {
        return [this.defaultActionForStop()];
    }

    availableStopStateOptions(): string[] {
        if (this.isCancelled()) {
            return [];
        }
        if (this.selectedStop?.stopPurpose === 'REJECTION_RETURN') {
            if (!this.selectedStop.returnDestinationConfirmedAt) return [];
            switch (String(this.selectedStop.workflowState || 'PENDING').toUpperCase()) {
                case 'PENDING': return ['ARRIVED'];
                case 'ARRIVED': return ['WORKING', 'COMPLETE'];
                case 'WORKING':
                case 'EVIDENCE_PENDING': return ['COMPLETE'];
                default: return [];
            }
        }
        switch (String(this.selectedStop?.workflowState || 'PENDING').toUpperCase()) {
            case 'PENDING':
                return ['ARRIVED'];
            case 'ARRIVED':
                return ['WORKING'];
            case 'WORKING':
            case 'EVIDENCE_PENDING':
                return ['COMPLETE'];
            default:
                return [];
        }
    }

    onNewLineActionChange(action: string): void {
        this.newLineAction = action;
        this.autoSelectCounterpartStop();
    }

    counterpartStops(): ShipmentDeliveryStop[] {
        const counterpartType = this.counterpartStopType();
        if (!counterpartType) {
            return [];
        }
        return this.stops
            .filter(stop => stop.stopId !== this.stopId)
            .filter(stop => String(stop.stopType || '').toUpperCase() === counterpartType)
            .sort((left, right) => Number(left.stopNumber ?? 0) - Number(right.stopNumber ?? 0));
    }

    counterpartFieldLabel(): string {
        const type = this.counterpartStopType();
        if (type === 'PICKUP') {
            return 'Paired pickup';
        }
        if (type === 'DROPOFF') {
            return 'Paired dropoff';
        }
        return 'Paired stop';
    }

    private autoSelectCounterpartStop(): void {
        const options = this.counterpartStops();
        if (!options.length) {
            this.newLineCounterpartStopId = undefined;
            return;
        }
        if (this.newLineCounterpartStopId && options.some(stop => stop.stopId === this.newLineCounterpartStopId)) {
            return;
        }
        this.newLineCounterpartStopId = this.nearestCounterpartStop(options)?.stopId;
    }

    private nearestCounterpartStop(options: ShipmentDeliveryStop[]): ShipmentDeliveryStop | undefined {
        const currentNumber = Number(this.selectedStop?.stopNumber ?? 0);
        const action = this.normalizedAction(this.newLineAction);
        if (action === 'PICKUP') {
            return options.find(stop => Number(stop.stopNumber ?? 0) > currentNumber) ?? options[0];
        }
        if (action === 'DROPOFF') {
            return [...options].reverse().find(stop => Number(stop.stopNumber ?? 0) < currentNumber)
                ?? options[options.length - 1];
        }
        return [...options].sort((left, right) =>
            Math.abs(Number(left.stopNumber ?? 0) - currentNumber) - Math.abs(Number(right.stopNumber ?? 0) - currentNumber)
        )[0];
    }

    private counterpartStopType(): 'PICKUP' | 'DROPOFF' | undefined {
        const action = this.normalizedAction(this.newLineAction || this.defaultActionForStop());
        if (action === 'PICKUP') {
            return 'DROPOFF';
        }
        if (action === 'DROPOFF') {
            return 'PICKUP';
        }
        return undefined;
    }

    addCounterpartLineIfSelected(): void {
        if (!this.newLineCounterpartStopId || !this.newLineShipmentLineId) {
            return;
        }
        const counterpart = this.stops.find(stop => stop.stopId === this.newLineCounterpartStopId);
        if (!counterpart) {
            return;
        }
        counterpart.lineItems = counterpart.lineItems ?? [];
        const counterpartAction = this.newLineAction === 'PICKUP' ? 'DROPOFF'
            : this.newLineAction === 'DROPOFF' ? 'PICKUP'
                : undefined;
        if (!counterpartAction) {
            return;
        }
        if (this.hasStopLine(counterpart, this.newLineShipmentLineId, counterpartAction)) {
            return;
        }
        counterpart.lineItems.push({
            shipmentId: this.shipment.shipmentId,
            deliveryId: this.deliveryId,
            stopId: counterpart.stopId,
            shipmentLineId: this.newLineShipmentLineId,
            deliveryLineId: this.findDeliveryLineId(this.newLineShipmentLineId),
            action: counterpartAction,
            plannedQuantity: Number(this.newLinePlannedQuantity || 0),
            actualQuantity: 0,
            workflowState: 'PLANNED'
        });
    }

    allStopLines(): ShipmentDeliveryStopLine[] {
        return this.stops.flatMap(stop => (stop.lineItems ?? []).map(line => ({
            ...line,
            stopId: stop.stopId,
            shipmentId: this.shipment.shipmentId,
            deliveryId: this.deliveryId
        })));
    }

    validStopLinesForSave(): ShipmentDeliveryStopLine[] {
        return this.allStopLines().filter(line => Number(line.plannedQuantity ?? 0) > 0 || Number(line.actualQuantity ?? 0) > 0);
    }

    private hasStopLine(stop: ShipmentDeliveryStop, shipmentLineId: number, action: string): boolean {
        const normalizedAction = this.normalizedAction(action);
        return (stop.lineItems ?? []).some(line =>
            line.shipmentLineId === shipmentLineId
            && this.normalizedAction(line.action || '') === normalizedAction
            && (Number(line.plannedQuantity ?? 0) > 0 || Number(line.actualQuantity ?? 0) > 0)
        );
    }

    payloadBalances(): PayloadBalanceRow[] {
        const rows = new Map<number, PayloadBalanceRow>();
        for (const stop of [...this.stops].sort((a, b) => (a.stopNumber ?? 0) - (b.stopNumber ?? 0))) {
            for (const line of stop.lineItems ?? []) {
                if (!line.shipmentLineId) {
                    continue;
                }
                const row = rows.get(line.shipmentLineId) ?? {
                    shipmentLineId: line.shipmentLineId,
                    label: this.payloadLabelByLineId(line.shipmentLineId),
                    plannedPickup: 0,
                    plannedDropoff: 0,
                    actualPickup: 0,
                    actualDropoff: 0,
                    plannedBalance: 0,
                    actualBalance: 0
                };
                const planned = Number(line.plannedQuantity ?? 0);
                const actual = Number(line.actualQuantity ?? 0);
                if (line.action === 'PICKUP') {
                    row.plannedPickup += planned;
                    row.actualPickup += actual;
                } else if (line.action === 'DROPOFF') {
                    row.plannedDropoff += planned;
                    row.actualDropoff += actual;
                }
                row.plannedBalance = row.plannedPickup - row.plannedDropoff;
                row.actualBalance = row.actualPickup - row.actualDropoff;
                rows.set(line.shipmentLineId, row);
            }
        }
        return [...rows.values()];
    }

    defaultActionForStop(): string {
        const stopType = this.selectedStop?.stopType?.toUpperCase();
        if (stopType?.includes('DROP')) {
            return 'DROPOFF';
        }
        return 'PICKUP';
    }

    payloadStateOptions(line?: ShipmentDeliveryStopLine): string[] {
        return this.normalizedAction(line?.action || this.newLineAction || this.defaultActionForStop()) === 'DROPOFF'
            ? this.dropoffPayloadStateOptions
            : this.pickupPayloadStateOptions;
    }

    availablePayloadStateOptions(line: ShipmentDeliveryStopLine): string[] {
        if (this.isCancelled()) {
            return [];
        }
        if (!['WORKING', 'EVIDENCE_PENDING'].includes(String(this.selectedStop?.workflowState || '').toUpperCase())) return [];
        if (this.remainingDecisionQuantity(line) <= 0 && this.selectedStop?.stopPurpose !== 'REJECTION_RETURN') return [];
        if (this.selectedStop?.stopPurpose === 'REJECTION_RETURN') {
            return this.selectedStop.returnDestinationConfirmedAt && this.currentPayloadState(line) !== 'DELIVERED'
                ? ['DELIVERED'] : [];
        }
        const current = this.currentPayloadState(line);
        const action = this.normalizedAction(line.action || this.defaultActionForStop());
        if (this.isPayloadTerminalState(current)) {
            return [];
        }
        const options = this.payloadStateOptions(line);
        if (action === 'DROPOFF') {
            return options.filter(state => state !== current);
        }
        return options.filter(state => {
            if (state === current) {
                return false;
            }
            if (this.payloadNormalStateRank(state) > -1 && this.payloadNormalStateRank(current) > -1) {
                return this.payloadNormalStateRank(state) > this.payloadNormalStateRank(current);
            }
            return true;
        });
    }

    currentPayloadState(line: ShipmentDeliveryStopLine): string {
        return this.normalizedAction(line.workflowState || 'PLANNED');
    }

    isPayloadLineTransitioning(line: ShipmentDeliveryStopLine): boolean {
        return !!line.stopLineId && this.transitioningStopLineIds.has(line.stopLineId);
    }

    payloadStateLabel(state: string): string {
        if (state === 'DELIVERED') {
            return 'Unloaded / Delivered';
        }
        return this.displayState(state);
    }

    payloadStateIcon(state: string): string {
        switch (state) {
            case 'PICKED': return 'fa-solid fa-dolly';
            case 'LOADED': return 'fa-solid fa-truck-ramp-box';
            case 'DELIVERED': return 'fa-solid fa-box-open';
            case 'SHORT': return 'fa-solid fa-scale-unbalanced';
            case 'DAMAGED': return 'fa-solid fa-boxes-packing';
            case 'EXCEPTION': return 'fa-solid fa-triangle-exclamation';
            default: return 'fa-solid fa-circle';
        }
    }

    payloadQuantitySummary(line: ShipmentDeliveryStopLine): string {
        const actual = Number(line.actualQuantity ?? 0);
        const planned = Number(line.plannedQuantity ?? 0);
        return `Actual ${actual} / Planned ${planned}`;
    }

    private actualQuantityForPayloadTransition(line: ShipmentDeliveryStopLine, targetState: string): number | undefined | null {
        const target = this.normalizedAction(targetState);
        const actual = this.numericQuantity(line.actualQuantity);
        if (!this.isStandardPayloadHandlingState(target)) {
            return actual;
        }
        if (actual != null && actual > 0) {
            return actual;
        }
        const planned = this.numericQuantity(line.plannedQuantity);
        if (planned != null && planned > 0) {
            line.actualQuantity = planned;
            return planned;
        }
        this.messageService.add({
            severity: 'warn',
            summary: 'Actual quantity required',
            detail: `Enter Actual Qty before recording ${this.payloadStateLabel(target)}.`
        });
        return null;
    }

    private numericQuantity(value?: number): number | undefined {
        if (value == null || (value as any) === '') {
            return undefined;
        }
        const quantity = Number(value);
        return Number.isFinite(quantity) ? quantity : undefined;
    }

    private isStandardPayloadHandlingState(state: string): boolean {
        return ['PICKED', 'LOADED', 'DELIVERED'].includes(state);
    }

    private isPayloadTerminalState(state: string): boolean {
        return ['LOADED', 'DELIVERED', 'SHORT', 'DAMAGED', 'EXCEPTION', 'REJECTED', 'RESCHEDULED'].includes(state);
    }

    private payloadNormalStateRank(state: string): number {
        return ['PLANNED', 'PICKED', 'LOADED'].indexOf(state);
    }

    stopActionLabel(state: string): string {
        const pickup = this.defaultActionForStop() === 'PICKUP';
        switch (state) {
            case 'ARRIVED': return 'Arrived';
            case 'WORKING': return pickup ? 'Start Loading' : 'Start Unloading';
            case 'EVIDENCE_PENDING': return pickup ? 'Loading Evidence' : 'Unloading Evidence';
            case 'COMPLETE': return pickup ? 'Loading Complete' : 'Unloading Complete';
            case 'SKIPPED': return 'Skipped';
            case 'EXCEPTION': return 'Exception';
            default: return this.displayState(state);
        }
    }

    stopActionIcon(state: string): string {
        const pickup = this.defaultActionForStop() === 'PICKUP';
        switch (state) {
            case 'ARRIVED': return 'fa-solid fa-location-dot';
            case 'WORKING': return pickup ? 'fa-solid fa-truck-ramp-box' : 'fa-solid fa-box-open';
            case 'EVIDENCE_PENDING': return 'fa-solid fa-camera';
            case 'COMPLETE': return 'fa-solid fa-circle-check';
            case 'SKIPPED': return 'fa-solid fa-forward-step';
            case 'EXCEPTION': return 'fa-solid fa-triangle-exclamation';
            default: return 'fa-solid fa-circle';
        }
    }

    evidenceActionLabel(): string {
        return this.defaultActionForStop() === 'PICKUP' ? 'Add Loading Evidence' : 'Add Unloading/POD Evidence';
    }

    evidenceActionIcon(): string {
        return this.defaultActionForStop() === 'PICKUP' ? 'fa-solid fa-camera' : 'fa-solid fa-file-signature';
    }

    evidenceUploadFileType(): string {
        return this.defaultActionForStop() === 'PICKUP' ? 'Loading Evidence' : 'Proof of Delivery';
    }

    evidenceType(): string {
        return this.defaultActionForStop() === 'PICKUP' ? 'LOADING_DOCUMENT' : 'POD_DOCUMENT';
    }

    evidenceSuccessMessage(): string {
        return this.defaultActionForStop() === 'PICKUP' ? 'Loading evidence uploaded.' : 'Unloading evidence uploaded.';
    }

    payloadEvidenceActionLabel(line: ShipmentDeliveryStopLine): string {
        return this.normalizedAction(line.action || this.defaultActionForStop()) === 'DROPOFF'
            ? 'Add Unloading/POD Evidence'
            : 'Add Loading Evidence';
    }

    payloadEvidenceActionIcon(line: ShipmentDeliveryStopLine): string {
        return this.normalizedAction(line.action || this.defaultActionForStop()) === 'DROPOFF'
            ? 'fa-solid fa-file-signature'
            : 'fa-solid fa-camera';
    }

    payloadEvidenceUploadFileType(line: ShipmentDeliveryStopLine): string {
        return this.normalizedAction(line.action || this.defaultActionForStop()) === 'DROPOFF'
            ? 'Proof of Delivery'
            : 'Loading Evidence';
    }

    payloadEvidenceType(line: ShipmentDeliveryStopLine): string {
        return this.normalizedAction(line.action || this.defaultActionForStop()) === 'DROPOFF'
            ? 'POD_DOCUMENT'
            : 'LOADING_DOCUMENT';
    }

    payloadEvidenceSuccessMessage(line: ShipmentDeliveryStopLine): string {
        return this.normalizedAction(line.action || this.defaultActionForStop()) === 'DROPOFF'
            ? 'Payload unloading evidence uploaded.'
            : 'Payload loading evidence uploaded.';
    }

    payloadEvidenceForLine(line: ShipmentDeliveryStopLine): ShipmentEvidence[] {
        return (this.deliveryEvidence ?? [])
            .filter(evidence => String(evidence.scope || '').toUpperCase() === 'PAYLOAD_LINE')
            .filter(evidence => Number(evidence.stopId) === Number(this.selectedStop?.stopId))
            .filter(evidence => String(evidence.evidenceType || '').toUpperCase() === this.payloadEvidenceType(line))
            .filter(evidence => {
                if (line.deliveryLineId && evidence.deliveryLineId) {
                    return Number(evidence.deliveryLineId) === Number(line.deliveryLineId);
                }
                return Number(evidence.shipmentLineId) === Number(line.shipmentLineId);
            })
            .sort((left, right) => Number(new Date(right.createdAt ?? 0)) - Number(new Date(left.createdAt ?? 0)));
    }

    hasPayloadEvidenceForLine(line: ShipmentDeliveryStopLine): boolean {
        return (this.deliveryEvidence ?? []).some(evidence =>
            String(evidence.scope || '').toUpperCase() === 'PAYLOAD_LINE'
            && String(evidence.status || '').toUpperCase() === 'VERIFIED'
            && Number(evidence.stopId) === Number(this.selectedStop?.stopId)
            && (evidence.deliveryLineId != null && Number(evidence.deliveryLineId) === Number(line.deliveryLineId)
                || evidence.shipmentLineId != null && Number(evidence.shipmentLineId) === Number(line.shipmentLineId)
                || evidence.scopeId != null && Number(evidence.scopeId) === Number(line.stopLineId)));
    }

    isDropoffLine(line: ShipmentDeliveryStopLine): boolean {
        return this.normalizedAction(line.action || this.defaultActionForStop()) === 'DROPOFF';
    }

    payloadEvidenceFileName(evidence: ShipmentEvidence): string {
        return evidence.valueText || evidence.evidenceUrl?.split('/').pop()?.split('?')[0] || 'Payload evidence';
    }

    payloadEvidenceIcon(evidence: ShipmentEvidence): string {
        const name = this.payloadEvidenceFileName(evidence).toLowerCase();
        if (/\.(jpg|jpeg|png|gif|webp|heic|heif)$/.test(name)) return 'fa-solid fa-image';
        if (/\.(mp4|mov|avi|mkv|webm)$/.test(name)) return 'fa-solid fa-video';
        if (/\.(xls|xlsx|csv)$/.test(name)) return 'fa-solid fa-file-excel';
        if (/\.(ppt|pptx)$/.test(name)) return 'fa-solid fa-file-powerpoint';
        if (/\.(doc|docx|rtf|txt)$/.test(name)) return 'fa-solid fa-file-word';
        if (name.endsWith('.pdf')) return 'fa-solid fa-file-pdf';
        return this.payloadEvidenceType({ action: evidence.evidenceType?.includes('POD') ? 'DROPOFF' : 'PICKUP' }) === 'POD_DOCUMENT'
            ? 'fa-solid fa-file-signature'
            : 'fa-solid fa-file-lines';
    }

    evidenceSectionTitle(): string {
        return this.defaultActionForStop() === 'PICKUP' ? 'Loading Evidence' : 'Unloading / POD Evidence';
    }

    evidenceEmptyMessage(): string {
        return this.defaultActionForStop() === 'PICKUP'
            ? 'No loading evidence uploaded for this pickup yet.'
            : 'No unloading or POD evidence uploaded for this dropoff yet.';
    }

    evidenceAttachmentIcon(file: ShipmentDeliveryAttachment): string {
        const name = String(file?.fileName || '').toLowerCase();
        const type = String(file?.fileType || '').toLowerCase();
        if (type.includes('image') || /\.(jpg|jpeg|png|gif|webp|heic|heif)$/.test(name)) return 'fa-solid fa-image';
        if (type.includes('video') || /\.(mp4|mov|avi|mkv|webm)$/.test(name)) return 'fa-solid fa-video';
        if (type.includes('excel') || /\.(xls|xlsx|csv)$/.test(name)) return 'fa-solid fa-file-excel';
        if (type.includes('powerpoint') || /\.(ppt|pptx)$/.test(name)) return 'fa-solid fa-file-powerpoint';
        if (type.includes('word') || /\.(doc|docx|rtf|txt)$/.test(name)) return 'fa-solid fa-file-word';
        if (type.includes('pdf') || name.endsWith('.pdf')) return 'fa-solid fa-file-pdf';
        return 'fa-solid fa-file-lines';
    }

    formatBytes(value?: number): string {
        const bytes = Number(value ?? 0);
        if (!bytes || Number.isNaN(bytes)) return '-';
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }

    private normalizedAction(action?: string): string {
        return String(action || '').toUpperCase();
    }

    private evidenceAttachmentsFromDelivery(): ShipmentDeliveryAttachment[] {
        const files = (this.shipmentDelivery?.filesList ?? [])
            .filter(file => Number(file.stopId) === Number(this.stopId));
        return this.filterEvidenceAttachments(files);
    }

    private filterEvidenceAttachments(files: ShipmentDeliveryAttachment[]): ShipmentDeliveryAttachment[] {
        const seen = new Set<string>();
        return files
            .filter(file => Number(file.stopId) === Number(this.stopId) || file.stopId == null)
            .filter(file => this.isCurrentStopEvidenceAttachment(file))
            .filter(file => {
                const key = this.evidenceAttachmentKey(file);
                if (seen.has(key)) {
                    return false;
                }
                seen.add(key);
                return true;
            })
            .sort((left, right) => Number(right.creationDate ?? 0) - Number(left.creationDate ?? 0));
    }

    private isCurrentStopEvidenceAttachment(file: ShipmentDeliveryAttachment): boolean {
        const type = String(file?.fileType || '').toLowerCase();
        const name = String(file?.fileName || '').toLowerCase();
        if (this.defaultActionForStop() === 'PICKUP') {
            return type === 'loading evidence'
                || type === 'loading_document'
                || type.includes('loading evidence')
                || name.includes('loading-evidence')
                || name.includes('loading_evidence');
        }
        return type === 'proof of delivery'
            || type === 'unloading evidence'
            || type === 'pod_document'
            || type === 'signed_pod'
            || type.includes('proof of delivery')
            || type.includes('unloading evidence')
            || name.includes('pod')
            || name.includes('proof-of-delivery')
            || name.includes('proof_of_delivery');
    }

    private upsertEvidenceAttachment(file: ShipmentDeliveryAttachment): ShipmentDeliveryAttachment[] {
        const key = this.evidenceAttachmentKey(file);
        const existing = this.stopEvidenceAttachments.filter(item => {
            return this.evidenceAttachmentKey(item) !== key;
        });
        return this.filterEvidenceAttachments([file, ...existing]);
    }

    private upsertDeliveryAttachment(file: ShipmentDeliveryAttachment): ShipmentDeliveryAttachment[] {
        const key = this.evidenceAttachmentKey(file);
        const existing = (this.shipmentDelivery?.filesList ?? []).filter(item => {
            return this.evidenceAttachmentKey(item) !== key;
        });
        return [file, ...existing];
    }

    private evidenceAttachmentKey(file: ShipmentDeliveryAttachment): string {
        const normalizedUrl = this.normalizedEvidenceUrl(file.fileUrl);
        if (normalizedUrl) {
            return `url:${normalizedUrl}`;
        }
        if (file.attachmentId) {
            return `id:${file.attachmentId}`;
        }
        return [
            'file',
            file.stopId ?? this.stopId ?? '',
            String(file.fileType || '').trim().toLowerCase(),
            String(file.fileName || '').trim().toLowerCase(),
            Number(file.fileSize ?? 0)
        ].join(':');
    }

    private normalizedEvidenceUrl(fileUrl?: string): string {
        if (!fileUrl) {
            return '';
        }
        try {
            const url = new URL(fileUrl);
            return `${url.origin}${url.pathname}`.toLowerCase();
        } catch {
            return fileUrl.split('?')[0].trim().toLowerCase();
        }
    }

    private upsertDeliveryEvidence(evidence: ShipmentEvidence): ShipmentEvidence[] {
        const key = evidence.evidenceId ? `id:${evidence.evidenceId}` : `url:${evidence.evidenceUrl}`;
        const existing = (this.deliveryEvidence ?? []).filter(item => {
            const itemKey = item.evidenceId ? `id:${item.evidenceId}` : `url:${item.evidenceUrl}`;
            return itemKey !== key;
        });
        return [evidence, ...existing];
    }

    private isInvalidStopTransition(message: string): boolean {
        return message.toLowerCase().includes('invalid stop workflow transition');
    }

    private isPayloadInputValidationError(message: string): boolean {
        const normalized = message.toLowerCase();
        return normalized.includes('payload delivery requires an actual delivered quantity')
            || normalized.includes('payload exception states require an exception reason')
            || normalized.includes('handle, reject, or reschedule every payload quantity before completing the site');
    }

    displayState(value?: string): string {
        if (!value) return 'Pending';
        const text = value.toLowerCase().replaceAll('_', ' ');
        return text.charAt(0).toUpperCase() + text.slice(1);
    }

    private hasAnyStopLine(): boolean {
        return this.stops.some(stop => (stop.lineItems ?? []).length > 0);
    }

    private activeDeliveryCount(): number {
        return (this.shipment?.deliveries ?? []).filter(delivery => {
            const workflowState = String(delivery.workflowState || '').toUpperCase();
            const jobStatus = String(delivery.jobStatus || '').toUpperCase();
            return workflowState !== 'CANCELLED'
                && jobStatus !== 'JOB_DRIVER_CANCELLED'
                && jobStatus !== 'JOB_DRIVER_REJECTED';
        }).length;
    }

    formatDate(value: string | number | Date | undefined): string {
        if (!value) {
            return '-';
        }
        const time = value instanceof Date ? value.getTime() : new Date(value).getTime();
        return Number.isNaN(time) ? '-' : formatDate(new Date(time), 'dd-MM-yyyy HH:mm', 'en-US');
    }

    getStatusSeverity(status: string | undefined): string {
        if (!status) return 'secondary';
        switch (status.toLowerCase()) {
            case 'complete':
            case 'delivered':
            case 'delivery_complete':
            case 'customer_acknowledged':
            case 'picked':
            case 'loaded': return 'success';
            case 'arrived':
            case 'working':
            case 'assigned':
            case 'accepted': return 'info';
            case 'evidence_pending':
            case 'in_transit':
            case 'planned': return 'warning';
            case 'exception':
            case 'short':
            case 'damaged':
            case 'skipped':
            case 'cancelled': return 'danger';
            default: return 'secondary';
        }
    }
}
